// noinspection JSUnusedGlobalSymbols

import truncate from 'lodash/truncate';
import type {BadgeVariants} from "@/shared/components/ui/badge";
import {useI18n} from "vue-i18n";
import type {StatusVariants} from "@/shared/components/ui/status";
import {Comment, Fragment, Text} from 'vue'
import {faGlobe, faLocationDot, faPeopleGroup, faPhone} from "@fortawesome/pro-regular-svg-icons";
import {faHandshake, faHeadset, faPencil, faStore, faTruck} from "@fortawesome/pro-solid-svg-icons";
import {CalendarDate, DateValue} from '@internationalized/date';
import {Customer, CustomerStatus, DeliveryMethod, Unit, PaymentMethod, UnitQuantity} from "@/shared/graphql/types";
import {parseISO} from "date-fns";
import {format, zonedTimeToUtc} from "date-fns-tz";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {faShieldCheck, faUserMagnifyingGlass, faBan} from "@fortawesome/pro-regular-svg-icons";

export function formatNumber(number: number, decimals: number = null, minimumFractions: number = null): string {
    const options = {
        minimumFractionDigits: minimumFractions ?? 0,
        maximumFractionDigits: decimals !== undefined ? decimals : 0,
    };
    return new Intl.NumberFormat('nl-NL', options).format(number);
}

export function isNumber(str: string): boolean {
    if (typeof str !== 'string') {
        return false;
    }

    if (str.trim() === '') {
        return false;
    }

    return !Number.isNaN(Number(str));
}

export function formatUnitQuantity(quantity: number, unit: Unit): string {
    const {t} = useI18n()
    return t('unit.info_quantity_resembles', {
        quantity: formatNumber(quantity, unit.supportsDecimals ? 2 : null),
        unit: (quantity == 1 ? unit.label : unit.plural).toLowerCase()
    })
}

export const createSubtitle = (name: String, count: Number): String => {
    if (count == 0) return ""
    const {t} = useI18n()
    return t(`${name}.more`, {count: count})
}

export function onlyOnce(fun: (...args: any[]) => any): (...args: any[]) => any {
    let counter = 0
    return (args) => {
        ++counter
        if (counter == 1) {
            fun(args)
        } else {
            console.log("Undo already called")
        }
    }
}

export function changeQueryVariable(keyString, replaceString) {
    let query = window.location.search.substring(1);
    let lets = query.split("&");
    let replaced = false;
    for (let i = 0; i < lets.length; i++) {
        let pair = lets[i].split("=");
        if (pair[0] == keyString) {
            lets[i] = pair[0] + "=" + replaceString;
            replaced = true;
        }
    }
    if (!replaced) lets.push(keyString + "=" + replaceString);
    return window.location.href.split("?")[0] + "?" + lets.join("&");
}

export enum Os {
    Mac,
    Other
}

export function getOS(): Os {
    // @ts-ignore
    const platform = (window.navigator?.userAgentData?.platform || window.navigator.platform).toLowerCase()
    const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

    if (platform.indexOf("mac") !== -1) {
        return Os.Mac;
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        return Os.Mac
    } else {
        return Os.Other
    }
}

export function capitalize(str: string): string {
    if (str == null) return ""
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function dedup(value: String) {
    if (value === undefined) return ""
    let words = value.split(' ');
    let uniqueWordsArray: string[] = [];

    for (let word of words) {
        if (!uniqueWordsArray.includes(word)) {
            uniqueWordsArray.push(word);
        }
    }
    return uniqueWordsArray.join(' ');
}

export function delay(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
}

export const limitLength = (text: string, length: number) => {
    if (text == "") return ""
    // noinspection JSAnnotator
    return truncate(text, {length: length, separator: /,? +/}) + '...';
}

export function openUrl(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank'; // Opens the link in a new tab
    link.rel = 'noopener noreferrer'; // Security best practice for opening untrusted links
    document.body.appendChild(link);
    link.click(); // Programmatically trigger the click event
    document.body.removeChild(link); // Clean up the anchor element
}

export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    keyGenerator?: (...args: Parameters<T>) => string
): (...args: Parameters<T>) => void {
    const timeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

    return function executedFunction(this: any, ...args: Parameters<T>): void {
        const context = this;
        const argsKey = keyGenerator ? keyGenerator(...args) : 'defaultKey';

        const later = (): void => {
            timeouts.delete(argsKey);
            func.apply(context, args);
        };

        if (timeouts.has(argsKey)) {
            clearTimeout(timeouts.get(argsKey));
        }

        timeouts.set(argsKey, setTimeout(later, wait));
    };
}

export function stripHtmlTags(html: string): string {
    return html.replace(/<[^>]*>/g, '');
}

export const generateUUID = () => {
    let
        d = new Date().getTime(),
        d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        let r = Math.random() * 16;
        if (d > 0) {
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c == 'x' ? r : (r & 0x7 | 0x8)).toString(16);
    });
};

export const stringToHexColor = (input: string): string => {
    // Convert the input string to a hash number
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
        hash = input.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Convert the hash to a hexadecimal color code
    let color = '#';
    let rgb = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        rgb[i] = value;
        color += ('00' + value.toString(16)).substr(-2);
    }

    // Calculate the brightness of the color
    const brightness = Math.sqrt(
        0.299 * Math.pow(rgb[0], 2) +
        0.587 * Math.pow(rgb[1], 2) +
        0.114 * Math.pow(rgb[2], 2)
    );

    // Darken the color if brightness is too high for white text readability
    if (brightness > 180) {
        for (let i = 0; i < 3; i++) {
            rgb[i] = Math.floor(rgb[i] * 0.7); // Darken each channel by 30%
            color = color.substr(0, i * 2 + 1) + ('00' + rgb[i].toString(16)).substr(-2) + color.substr(i * 2 + 3);
        }
    }

    return color;
}

export const customerStatusToBadgeVariant = (input: string): BadgeVariants['variant'] => {
    if (input == 'ACTIVE') return 'success'
    if (input == 'PROSPECT') return 'info'
    if (input == 'BLOCKED') return 'destructive'
    return 'warning'
}

export const stockLevelToBadgeVariant = (level: string): StatusVariants['variant'] => {
    switch (level) {
        case 'AVAILABLE':
            return 'success';
        case 'LIMITED':
            return 'warning';
        case 'OUT_OF_STOCK':
            return 'destructive';
        case 'UNKNOWN':
        default:
            return 'default';
    }
}
export const stockLevelToFontColor = (level: string): string => {
    switch (level) {
        case 'AVAILABLE':
            return 'text-green-500';
        case 'LIMITED':
            return 'text-yellow-500';
        case 'OUT_OF_STOCK':
            return 'text-red-400';
        case 'UNKNOWN':
        default:
            return 'text-gray-500';
    }
}

export function hasVisibleContent(vnodes) {
    if (!vnodes) return false;

    // Ensure vnodes is an array
    if (!Array.isArray(vnodes)) {
        vnodes = [vnodes];
    }

    for (const vnode of vnodes) {
        // Ignore comment nodes
        if (vnode.type === Comment) {
            continue;
        }

        // Check text nodes with non-whitespace content
        if (vnode.type === Text) {
            if (vnode.children && vnode.children.trim() !== '') {
                return true;
            } else {
                continue;
            }
        }

        // If it's a fragment, check its children recursively
        if (vnode.type === Fragment) {
            if (hasVisibleContent(vnode.children)) {
                return true;
            } else {
                continue;
            }
        }

        // For elements or components, check if they will render
        if (typeof vnode.type === 'object' || typeof vnode.type === 'string') {
            // Check if the vnode is excluded by v-if
            if (vnode.shapeFlag & 128) { // SUSPENSE
                continue;
            }

            if (vnode.shapeFlag & 256) { // TELEPORT
                continue;
            }

            // Check if the vnode has a directive with v-if set to false
            if (vnode.props && vnode.props['v-if'] === false) {
                continue;
            }

            // At this point, we assume the vnode represents visible content
            return true;
        }
    }

    // No visible content found
    return false;
}

export const convertNewlinesToBr = (text: string): string => {
    if (!text) return '';
    return text.replace(/\n/g, '<br>');
}

export const getIconByNoteType = (noteType: string): any => {
    switch (noteType) {
        case "FREE_NOTE":
            return faPencil;
        case "VISIT":
            return faHandshake;
        case "PHONE_CALL":
            return faPhone;
        case "ONLINE_MEETING":
            return faHeadset;
        default:
            return null; // Return null if the id doesn't match any
    }
}


export const getIconByAppointmentType = (appointmentType: string): any => {
    switch (appointmentType) {
        case "LOCATION":
            return faLocationDot;
        case "ONLINE":
            return faGlobe;
        case "PHONE":
            return faPhone;
        case "INTERNAL":
            return faPeopleGroup;
        default:
            return null; // Return null if the id doesn't match any
    }
}

export const toDateValue = (date: Date): DateValue => {
    if (!date) return null
    // Extract components of the date
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // month is 0-indexed in JavaScript Date
    const day = date.getDate();

    // Assuming DateValue expects an object with year, month, and day
    return new CalendarDate(year, month, day)
}

export const formatTime = (dateTime?: Date | string): string => {
    if (!dateTime) return '';

    if (typeof dateTime === 'string') {
        const isoDateTime = dateTime.length === 16 ? `${dateTime}:00` : dateTime;
        const parsedDate = parseISO(isoDateTime);
        return format(parsedDate, 'HH:mm');
    }

    const adjustedDate = new Date(dateTime);
    const offset = adjustedDate.getTimezoneOffset();
    adjustedDate.setMinutes(adjustedDate.getMinutes() + offset);
    return format(adjustedDate, 'HH:mm');
};


export const combineDateAndTime = (date: Date, time: string) => {
    // Convert input date to the Brussels time zone
    let localDate = zonedTimeToUtc(date, 'Europe/Brussels')

    let year = localDate.getFullYear();
    let month = localDate.getMonth();
    let day = localDate.getDate();
    let [hours, minutes] = time.split(':').map(Number);

    return new Date(year, month, day, hours, minutes);
}

export const goBack = () => {
    window.history.go(-1)
}

export function getDeliveryMethodIcon(method: DeliveryMethod) {
    if (method.kind === 'DELIVERY') {
        return faTruck
    } else if (method.kind === 'PICKUP') {
        return faStore
    }
    return faTruck
}

export function generateUniqueString(): string {
    return Math.random().toString(36).substring(2, 10); // Generate base-36 string and remove "0."
}

export function generateId(aggregationName: string, bucketValue: string, uid?: String): string {
    const suffix = uid ? `-${uid}-` : ''
    return `filter${suffix}-${aggregationName.replace(/[^a-zA-Z0-9-_]/g, '')}-${bucketValue.replace(/[^a-zA-Z0-9-_]/g, '')}`;
}

export const customerIcon = (customer: Customer): IconDefinition => {
    return customerIconByStatus(customer.status)
}
export const customerIconColor = (customer: Customer): string => {
    return customerIconColorByStatus(customer.status)
}

export const customerIconByStatus = (status: string): IconDefinition => {
    if (status == 'ACTIVE') return faShieldCheck
    if (status == 'PROSPECT') return faUserMagnifyingGlass
    if (status == 'BLOCKED') return faBan
}
export const customerIconColorByStatus = (status: string): string => {
    if (status == 'ACTIVE') return "text-green-600"
    if (status == 'PROSPECT') return "text-blue-600"
    if (status == 'BLOCKED') return "text-red-600"
}

export const paymentMethodLabel = (method: PaymentMethod) => {
    const {t} = useI18n()
    if (method.name === "INVOICE") return t('shared.checkout.invoice')
    if (method.name === "VOUCHER") return t('shared.checkout.voucher')
    if (method.name === "VOORAF_BETALEN") return t('shared.checkout.cache')
    return null
}